import {
  bindBeraterAbandonOnUnload,
  initBeraterAnalyticsSession,
  publishBeraterAbandon,
  publishBeraterLoadingViewed,
  publishBeraterRestart,
  publishBeraterStarted,
  publishBeraterStepAnswered,
} from "./analytics";
import { invalidateAccessoryPreviewCache, prepareAccessoryStep } from "./accessoryPreview";
import { validateGraph } from "./engine";
import { normalizeAccessoryMode, resolveLinkedProductHandle } from "./normalize";
import { renderResult } from "./resultRenderer";
import { openBusinessTrustpilotDrawer, renderBusinessTrustpilotStars } from "./trustpilot";
import type { BeraterAnswer, BeraterData, BeraterSessionState, BeraterStepData } from "./types";

const debugLog = (data: BeraterData, label: string, payload: unknown): void => {
  if (!data.debug) return;
  console.log(`[ek-berater] ${label}`, payload);
};

const STORAGE_PREFIX = "ek:berater:";
const RETURN_URL_PREFIX = "ek:berater:return:";
const LOADING_MS = 2800;
const OPTION_ADVANCE_MS = 300;
// Max steps on a single path (guards misconfigured cycles, not total session clicks)
const MAX_PATH_DEPTH = 32;

const isBeraterPagePath = (pathname: string): boolean => pathname.includes("-berater");

const readReturnUrl = (flowHandle: string): string | null => {
  const key = `${RETURN_URL_PREFIX}${flowHandle}`;
  const referrer = document.referrer;

  if (referrer) {
    try {
      const refUrl = new URL(referrer);
      if (
        refUrl.origin === window.location.origin &&
        refUrl.pathname !== window.location.pathname &&
        !isBeraterPagePath(refUrl.pathname)
      ) {
        sessionStorage.setItem(key, referrer);
        return referrer;
      }
    } catch {
      // ignore bad referrer
    }
  }

  return sessionStorage.getItem(key);
};

const activateBeraterFunnelChrome = (): void => {
  document.documentElement.classList.add("berater-funnel-active");

  const menuOpen = document.getElementById("mobile-mega-menu")?.getAttribute("aria-expanded") === "true";
  if (menuOpen) {
    document.getElementById("close-mobile-mega-menu")?.click();
  }
};

const syncBeraterQuizChrome = (panelId: string): void => {
  document.documentElement.classList.toggle("berater-quiz-active", panelId !== "result");
};

const focusablePanelTarget = (panel: HTMLElement, panelId: string, steps: Map<string, BeraterStepData>): HTMLElement | null => {
  if (steps.has(panelId)) {
    return panel.querySelector<HTMLElement>("[data-berater-step-headline]");
  }
  if (panelId === "intro") {
    const headline = panel.querySelector<HTMLElement>("[data-berater-intro-headline]");
    if (headline && !headline.hasAttribute("tabindex")) headline.tabIndex = -1;
    return headline;
  }
  if (panelId === "loading") {
    const heading = panel.querySelector<HTMLElement>(".berater-loading-copy");
    if (heading && !heading.hasAttribute("tabindex")) heading.tabIndex = -1;
    return heading;
  }
  return null;
};

const scrollBeraterToTop = (): void => {
  if (!window.matchMedia("(max-width: 1023px)").matches) return;
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
};

const normalizeBeraterData = (data: BeraterData): BeraterData => ({
  ...data,
  steps: data.steps.map((step) => ({
    ...step,
    options: step.options.map((option) => ({
      ...option,
      accessoryMode: normalizeAccessoryMode(String(option.accessoryMode ?? "none")),
      linkedProductHandle: resolveLinkedProductHandle(option),
      exclusiveGroup: option.exclusiveGroup?.trim() || null,
    })),
  })),
});

const parseBeraterData = (root: HTMLElement): BeraterData | null => {
  const script = root.querySelector<HTMLScriptElement>("script[data-berater-data]");
  if (!script?.textContent) return null;
  try {
    return normalizeBeraterData(JSON.parse(script.textContent) as BeraterData);
  } catch {
    return null;
  }
};

const storageKey = (flowHandle: string): string => `${STORAGE_PREFIX}${flowHandle}`;

const readSession = (flowHandle: string): BeraterSessionState | null => {
  try {
    const raw = sessionStorage.getItem(storageKey(flowHandle));
    if (!raw) return null;
    return JSON.parse(raw) as BeraterSessionState;
  } catch {
    return null;
  }
};

const writeSession = (flowHandle: string, state: BeraterSessionState): void => {
  sessionStorage.setItem(storageKey(flowHandle), JSON.stringify(state));
};

const clearSession = (flowHandle: string): void => {
  sessionStorage.removeItem(storageKey(flowHandle));
};

const stepMap = (data: BeraterData): Map<string, BeraterStepData> => new Map(data.steps.map((step) => [step.handle, step]));

const queryAll = <T extends Element>(root: ParentNode, selector: string): T[] =>
  Array.from(root.querySelectorAll(selector)) as T[];

class BeraterController {
  readonly #root: HTMLElement;
  readonly #data: BeraterData;
  readonly #steps: Map<string, BeraterStepData>;
  #path: string[] = [];
  #answers: BeraterAnswer[] = [];
  #panel = "intro";
  #historyPushCount = 0;
  #optionAdvanceTimeoutId: number | null = null;
  #loadingResultTimeoutId: number | null = null;
  #accessoryPrepareToken = 0;
  // Radios fire no `change` when the checked option is clicked again (e.g. after
  // navigating back) — a `click` listener advances in that case. This flag keeps
  // the click that BELONGS to a change event from advancing twice.
  #suppressClickAdvance = false;

  constructor(root: HTMLElement, data: BeraterData) {
    this.#root = root;
    this.#data = data;
    this.#steps = stepMap(data);
    initBeraterAnalyticsSession(data.flowHandle, Boolean(this.#data.debug));
    bindBeraterAbandonOnUnload(data.flowHandle, Boolean(data.debug), () => this.#analyticsState());
    this.#bindEvents();
    this.#formatDebugPoolDisplay();
    this.#initIntroTrustpilot();
    this.#restoreOrStart();
    debugLog(data, "init", {
      flowHandle: data.flowHandle,
      firstStep: data.firstStep,
      steps: data.steps.map((s) => ({ handle: s.handle, attribute: s.attribute })),
      candidates: data.candidates.map((c) => ({ handle: c.handle, title: c.title, attrs: c.attrs })),
    });
  }

  #initIntroTrustpilot(): void {
    const container = this.#root.querySelector<HTMLElement>("[data-berater-intro-trustpilot]");
    if (!container) return;

    const useWhiteStars =
      this.#root.classList.contains("berater-root--intro-dark") && !window.matchMedia("(min-width: 1024px)").matches;
    void renderBusinessTrustpilotStars(container, () => {
      openBusinessTrustpilotDrawer();
    }, useWhiteStars);
  }

  #bindEvents(): void {
    const introContinue = this.#root.querySelector<HTMLButtonElement>("[data-berater-intro-continue]");
    introContinue?.addEventListener("click", () => this.#startFlow());

    const debugCopyBtn = this.#root.querySelector<HTMLButtonElement>("[data-berater-debug-copy]");
    debugCopyBtn?.addEventListener("click", () => this.#copyDebugPool());

    const backBtn = this.#root.querySelector<HTMLButtonElement>("[data-berater-back]");
    backBtn?.addEventListener("click", () => this.#goBack());

    for (const closeBtn of queryAll<HTMLButtonElement>(this.#root, "[data-berater-close]")) {
      closeBtn.addEventListener("click", () => {
        const state = this.#analyticsState();
        publishBeraterAbandon(this.#data.flowHandle, Boolean(this.#data.debug), {
          lastPanel: state.panel,
          lastStepIndex: state.stepIndex,
          reachedResult: state.reachedResult,
          exitMethod: "close_button",
        });
        const fallbackUrl = closeBtn.dataset.beraterCloseUrl || this.#data.closeUrl || "/collections/betten";
        this.#exitToPreviousPage(fallbackUrl);
      });
    }

    // Result card HTML (incl. desktop restart) arrives via Section Rendering API after init
    this.#root.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target.closest("[data-berater-restart]") : null;
      if (!target || !this.#root.contains(target)) return;
      this.#restart();
    });

    const loadingContinueBtn = this.#root.querySelector<HTMLButtonElement>("[data-berater-loading-continue]");
    loadingContinueBtn?.addEventListener("click", () => this.#showResultFromLoading());

    for (const panel of queryAll<HTMLElement>(this.#root, "[data-berater-step]")) {
      const stepHandle = panel.dataset.beraterStep;
      if (!stepHandle) continue;

      for (const input of queryAll<HTMLInputElement>(panel, "[data-berater-option]")) {
        input.addEventListener("change", () => {
          this.#suppressClickAdvance = true;
          this.#onOptionChange(stepHandle, panel, input);
        });
        input.addEventListener("click", () => {
          if (this.#suppressClickAdvance) {
            this.#suppressClickAdvance = false;
            return;
          }
          const step = this.#steps.get(stepHandle);
          if (!step || step.multiSelect) return;
          // Re-click on the already-checked option (after "zurück"): advance again
          if (input.checked) this.#scheduleAdvanceFromStep(stepHandle);
        });
      }

      const continueBtn = panel.querySelector<HTMLButtonElement>("[data-berater-continue]");
      continueBtn?.addEventListener("click", () => this.#advanceFromStep(stepHandle));
    }

    window.addEventListener("popstate", () => this.#syncFromHistory());

    window.addEventListener("pageshow", (event) => {
      if (!event.persisted) return;
      this.#historyPushCount = 0;
      this.#syncFromHistory();
    });
  }

  #formatDebugPoolDisplay(): void {
    if (!this.#data.debug) return;
    const script = this.#root.querySelector<HTMLScriptElement>("script[data-berater-debug-pool]");
    const display = this.#root.querySelector<HTMLElement>("[data-berater-debug-pool-display]");
    const raw = script?.textContent?.trim();
    if (!raw || !display) return;

    try {
      display.textContent = JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      display.textContent = raw;
    }
  }

  #copyDebugPool(): void {
    const script = this.#root.querySelector<HTMLScriptElement>("script[data-berater-debug-pool]");
    const display = this.#root.querySelector<HTMLElement>("[data-berater-debug-pool-display]");
    const raw = script?.textContent?.trim();
    if (!raw) return;

    if (display) {
      try {
        display.textContent = JSON.stringify(JSON.parse(raw), null, 2);
      } catch {
        display.textContent = raw;
      }
    }

    const write = async (): Promise<void> => {
      await navigator.clipboard.writeText(display?.textContent ?? raw);
      debugLog(this.#data, "debug pool copied", { bytes: raw.length });
    };

    void write().catch(() => {
      // Clipboard blocked: pre is still readable/selectable
    });
  }

  #analyticsState(): { panel: string; stepIndex: number; reachedResult: boolean } {
    const stepIndex = this.#path.indexOf(this.#panel);
    return {
      panel: this.#panel,
      stepIndex: stepIndex >= 0 ? stepIndex + 1 : 0,
      reachedResult: this.#panel === "result" || this.#panel === "loading",
    };
  }

  #resultRenderKey(): string {
    return `${this.#data.flowHandle}:${JSON.stringify(this.#answers)}`;
  }

  #exitToPreviousPage(fallbackUrl: string): void {
    const returnUrl = readReturnUrl(this.#data.flowHandle);
    clearSession(this.#data.flowHandle);

    if (returnUrl) {
      publishBeraterAbandon(this.#data.flowHandle, Boolean(this.#data.debug), {
        lastPanel: this.#analyticsState().panel,
        lastStepIndex: this.#analyticsState().stepIndex,
        reachedResult: this.#analyticsState().reachedResult,
        exitMethod: "return_link",
      });
      window.location.assign(returnUrl);
      return;
    }

    if (this.#historyPushCount > 0) {
      const stepsBack = this.#historyPushCount + 1;
      if (window.history.length > stepsBack) {
        history.go(-stepsBack);
        return;
      }
    }

    window.location.assign(fallbackUrl);
  }

  #restoreOrStart(): void {
    const params = new URLSearchParams(window.location.search);
    const skipIntro = params.get("start") === "1";
    const entryFromPdp = params.get("ref")?.trim().toLowerCase() === "pdp";

    if (entryFromPdp) {
      clearSession(this.#data.flowHandle);
      this.#path = [];
      this.#answers = [];
      this.#clearSelections();
      if (skipIntro && this.#data.firstStep) {
        this.#startFlow(false, true);
        return;
      }
      this.#showPanel("intro", false);
      return;
    }

    const saved = readSession(this.#data.flowHandle);

    if (saved?.panel) {
      this.#path = saved.path ?? [];
      this.#answers = saved.answers ?? [];
      this.#applyAnswersToInputs();

      if (saved.panel === "loading" || saved.panel === "result") {
        if (this.#data.debug) {
          const lastStep = this.#path.length ? this.#path[this.#path.length - 1] : null;
          if (lastStep && this.#steps.has(lastStep)) {
            this.#showPanel(lastStep, false);
            return;
          }
          clearSession(this.#data.flowHandle);
          this.#path = [];
          this.#answers = [];
          this.#showPanel("intro", false);
          return;
        }

        void renderResult(this.#root, this.#data, this.#answers);
        this.#showPanel("result", false);
        this.#seedHistoryFromState("result", this.#path);
        return;
      }
      if (saved.panel === "intro" || this.#steps.has(saved.panel)) {
        this.#showPanel(saved.panel, false);
        this.#seedHistoryFromState(saved.panel, this.#path);
        return;
      }
      // Saved step no longer exists (content edit) — start over instead of a blank screen
      clearSession(this.#data.flowHandle);
      this.#path = [];
      this.#answers = [];
    }

    if (skipIntro && this.#data.firstStep) {
      this.#startFlow(false, true);
      return;
    }

    this.#showPanel("intro", false);
  }

  #startFlow(pushHistory = true, skipIntro = false): void {
    if (!this.#data.firstStep) return;
    this.#path = [this.#data.firstStep];
    this.#answers = [];
    publishBeraterStarted(this.#data.flowHandle, Boolean(this.#data.debug), skipIntro);
    this.#showPanel(this.#data.firstStep, pushHistory);
  }

  #restart(): void {
    publishBeraterRestart(this.#data.flowHandle, Boolean(this.#data.debug), this.#panel);
    this.#clearOptionAdvanceTimeout();
    this.#clearLoadingResultTimeout();
    clearSession(this.#data.flowHandle);
    this.#path = [];
    this.#answers = [];
    this.#historyPushCount = 0;
    history.replaceState({ beraterPanel: "intro" }, "");
    this.#showPanel("intro", false);
    this.#clearSelections();
  }

  #onOptionChange(stepHandle: string, panel: HTMLElement, changedInput?: HTMLInputElement): void {
    const step = this.#steps.get(stepHandle);
    if (!step) return;

    if (step.multiSelect && changedInput?.checked) {
      const group = changedInput.dataset.exclusiveGroup?.trim();
      if (group) {
        for (const other of queryAll<HTMLInputElement>(panel, "[data-berater-option]:checked")) {
          if (other === changedInput) continue;
          if (other.dataset.exclusiveGroup?.trim() === group) other.checked = false;
        }
      }
    }

    this.#updateSelectedStyles(panel);

    if (step.multiSelect) {
      const continueBtn = panel.querySelector<HTMLButtonElement>("[data-berater-continue]");
      const checked = panel.querySelectorAll<HTMLInputElement>("[data-berater-option]:checked");
      if (continueBtn) continueBtn.disabled = checked.length === 0;
      if (step.attribute === "accessories") {
        invalidateAccessoryPreviewCache();
      }
      return;
    }

    this.#scheduleAdvanceFromStep(stepHandle);
  }

  #clearOptionAdvanceTimeout(): void {
    if (this.#optionAdvanceTimeoutId !== null) {
      window.clearTimeout(this.#optionAdvanceTimeoutId);
      this.#optionAdvanceTimeoutId = null;
    }
  }

  #scheduleAdvanceFromStep(stepHandle: string): void {
    this.#clearOptionAdvanceTimeout();

    const panel = this.#root.querySelector<HTMLElement>(`[data-berater-panel="${stepHandle}"]`);
    const selected = panel?.querySelector<HTMLInputElement>("[data-berater-option]:checked");
    const nextFromOption = selected?.dataset.next?.trim();
    const defaultNext = panel?.dataset.beraterDefaultNext?.trim();
    const nextStep = nextFromOption || defaultNext || null;
    if (nextStep && this.#steps.get(nextStep)?.attribute === "accessories") {
      void prepareAccessoryStep(this.#root, this.#data, this.#answers, nextStep, { warmOnly: true });
    }

    this.#optionAdvanceTimeoutId = window.setTimeout(() => {
      this.#optionAdvanceTimeoutId = null;
      this.#advanceFromStep(stepHandle);
    }, OPTION_ADVANCE_MS);
  }

  #advanceFromStep(stepHandle: string): void {
    const step = this.#steps.get(stepHandle);
    const panel = this.#root.querySelector<HTMLElement>(`[data-berater-panel="${stepHandle}"]`);
    if (!step || !panel) return;

    const selected = queryAll<HTMLInputElement>(panel, "[data-berater-option]:checked");
    if (!selected.length) return;

    const values = selected.map((input) => input.dataset.value ?? input.value).filter(Boolean);
    this.#recordAnswer(stepHandle, step.attribute, values);

    publishBeraterStepAnswered(this.#data.flowHandle, Boolean(this.#data.debug), {
      stepHandle,
      stepAttribute: step.attribute,
      stepIndex: this.#answers.length,
      answerValues: values,
      multiSelect: step.multiSelect,
    });

    debugLog(this.#data, "answer", { stepHandle, attribute: step.attribute, values });

    const nextFromOption = selected[0]?.dataset.next?.trim();
    const defaultNext = panel.dataset.beraterDefaultNext?.trim();
    const nextStep = nextFromOption || defaultNext || null;

    // Truncate the path at the current step: re-answering after "zurück" discards the
    // abandoned branch, and answers from steps no longer on the path are pruned so they
    // can't contaminate filtering/scoring (e.g. groesse-jugend after switching to kleinkind).
    const currentIndex = this.#path.indexOf(stepHandle);
    const basePath = currentIndex >= 0 ? this.#path.slice(0, currentIndex) : [...this.#path];
    this.#path = nextStep ? [...basePath, stepHandle, nextStep] : [...basePath, stepHandle];
    this.#pruneAnswersToPath();

    debugLog(this.#data, "path", { path: this.#path, answers: this.#answers });

    if (this.#path.length > MAX_PATH_DEPTH) {
      this.#goToResult();
      return;
    }

    if (nextStep && this.#steps.has(nextStep)) {
      this.#showPanel(nextStep, true);
      return;
    }

    this.#goToResult();
  }

  #recordAnswer(stepHandle: string, attribute: BeraterAnswer["attribute"], values: string[]): void {
    this.#answers = this.#answers.filter((answer) => answer.stepHandle !== stepHandle);
    this.#answers.push({ stepHandle, attribute, values });
  }

  #clearLoadingResultTimeout(): void {
    if (this.#loadingResultTimeoutId !== null) {
      window.clearTimeout(this.#loadingResultTimeoutId);
      this.#loadingResultTimeoutId = null;
    }
  }

  #showResultFromLoading(): void {
    if (this.#panel !== "loading") return;
    this.#clearLoadingResultTimeout();
    this.#showPanel("result", true);
  }

  #goToResult(): void {
    invalidateAccessoryPreviewCache();
    publishBeraterLoadingViewed(this.#data.flowHandle, Boolean(this.#data.debug), this.#answers, this.#resultRenderKey());
    // Loading is transient: never push it into the history stack, otherwise
    // "zurück" from the result lands on a spinner that never resolves.
    this.#clearLoadingResultTimeout();
    this.#showPanel("loading", false);
    // Start fetching immediately so the interstitial covers the network time
    void renderResult(this.#root, this.#data, this.#answers);

    if (this.#data.debug) return;

    this.#loadingResultTimeoutId = window.setTimeout(() => {
      this.#loadingResultTimeoutId = null;
      this.#showPanel("result", true);
    }, LOADING_MS);
  }

  #showPanel(panelId: string, pushHistory: boolean): void {
    const step = this.#steps.get(panelId);
    if (step?.attribute === "accessories") {
      void this.#showAccessoryPanelWhenReady(panelId, pushHistory);
      return;
    }
    this.#activatePanel(panelId, pushHistory);
  }

  async #showAccessoryPanelWhenReady(panelId: string, pushHistory: boolean): Promise<void> {
    const token = ++this.#accessoryPrepareToken;
    debugLog(this.#data, "accessory panel prepare", { stepHandle: panelId, answers: this.#answers });

    try {
      await prepareAccessoryStep(this.#root, this.#data, this.#answers, panelId, { forceRefresh: !pushHistory });
    } catch (error) {
      console.error("[ek-berater] accessory preview prepare failed:", error);
    }

    if (token !== this.#accessoryPrepareToken) return;
    this.#activatePanel(panelId, pushHistory, { fromAccessoryPrepare: true });
  }

  #releaseFocusFromPanel(nextPanelId: string): void {
    const focused = document.activeElement;
    if (!(focused instanceof HTMLElement) || !this.#root.contains(focused)) return;

    const host = focused.closest<HTMLElement>("[data-berater-panel]");
    if (!host || host.dataset.beraterPanel === nextPanelId) return;
    focused.blur();
  }

  #focusActivePanel(panelId: string): void {
    const panel = this.#root.querySelector<HTMLElement>(`[data-berater-panel="${panelId}"]`);
    if (!panel) return;

    const target = focusablePanelTarget(panel, panelId, this.#steps);
    target?.focus({ preventScroll: true });
  }

  #activatePanel(panelId: string, pushHistory: boolean, options?: { fromAccessoryPrepare?: boolean }): void {
    if (!options?.fromAccessoryPrepare) {
      this.#accessoryPrepareToken += 1;
    }
    this.#clearOptionAdvanceTimeout();
    this.#clearLoadingResultTimeout();
    this.#panel = panelId;
    this.#suppressClickAdvance = false;

    this.#releaseFocusFromPanel(panelId);

    for (const panel of queryAll<HTMLElement>(this.#root, "[data-berater-panel]")) {
      const isActive = panel.dataset.beraterPanel === panelId;
      panel.classList.toggle("berater-panel--active", isActive);
      panel.toggleAttribute("inert", !isActive);
      if (isActive) {
        panel.setAttribute("aria-hidden", "false");
      } else {
        // ponytail: display:none + inert hide inactive panels; aria-hidden=true while focus
        // lingers on the clicked control triggers Chrome a11y warnings.
        panel.removeAttribute("aria-hidden");
      }
    }

    requestAnimationFrame(() => {
      this.#focusActivePanel(panelId);
    });

    scrollBeraterToTop();

    const isIntro = panelId === "intro";
    const isQuestion = this.#steps.has(panelId);
    const isResult = panelId === "result" || panelId === "loading";

    const stepperWrap = this.#root.querySelector<HTMLElement>("[data-berater-stepper-wrap]");
    const backBtn = this.#root.querySelector<HTMLButtonElement>("[data-berater-back]");

    if (stepperWrap) {
      stepperWrap.classList.toggle("hidden", !isQuestion);
    }

    if (backBtn) {
      backBtn.classList.toggle("hidden", !(isQuestion || isResult));
    }

    if (isIntro) {
      this.#path = [];
    }

    this.#renderStepper(panelId);
    this.#persist();

    if (pushHistory) {
      this.#historyPushCount += 1;
      history.pushState({ beraterPanel: panelId, beraterPath: this.#path }, "");
    }

    syncBeraterQuizChrome(panelId);
  }

  // Walk from first_step using recorded answers; at the first unanswered fork, project
  // the longest remaining branch so the dot count stays stable for the current path.
  #nextCandidatesFor(step: BeraterStepData, seen: Set<string>): string[] {
    const candidates = new Set<string>();
    for (const option of step.options) {
      if (option.nextStep && this.#steps.has(option.nextStep) && !seen.has(option.nextStep)) {
        candidates.add(option.nextStep);
      }
    }
    if (step.defaultNext && this.#steps.has(step.defaultNext) && !seen.has(step.defaultNext)) {
      candidates.add(step.defaultNext);
    }
    return [...candidates];
  }

  #extendStepperChain(startHandle: string, seen: Set<string>): string[] {
    const chain: string[] = [];
    let cursor: string | undefined = startHandle;

    while (cursor && !seen.has(cursor) && chain.length < MAX_PATH_DEPTH) {
      seen.add(cursor);
      chain.push(cursor);

      const step = this.#steps.get(cursor);
      if (!step) break;

      const answer = this.#answers.find((item) => item.stepHandle === cursor);
      if (answer) {
        const chosen = step.options.find((option) => answer.values.includes(option.value));
        const next = chosen?.nextStep ?? step.defaultNext;
        if (!next || !this.#steps.has(next) || seen.has(next)) break;
        cursor = next;
        continue;
      }

      const candidates = this.#nextCandidatesFor(step, seen);
      if (!candidates.length) break;

      let longestTail: string[] = [];
      for (const next of candidates) {
        const tail = this.#extendStepperChain(next, new Set(seen));
        if (tail.length > longestTail.length) longestTail = tail;
      }
      chain.push(...longestTail);
      break;
    }

    return chain;
  }

  #projectedPath(): string[] {
    if (!this.#data.firstStep) return [];
    return this.#extendStepperChain(this.#data.firstStep, new Set());
  }

  #renderStepper(activePanel: string): void {
    const list = this.#root.querySelector<HTMLElement>("[data-berater-stepper]");
    if (!list) return;

    const path = this.#projectedPath();
    const activeIndex = path.indexOf(activePanel);
    list.innerHTML = "";

    for (const [index, handle] of path.entries()) {
      const item = document.createElement("li");
      item.setAttribute("aria-label", `Schritt ${index + 1} von ${path.length}`);
      if (index === activeIndex) item.setAttribute("aria-current", "step");

      const dot = document.createElement("div");
      dot.className = "berater-stepper-dot";
      dot.setAttribute("aria-hidden", "true");
      dot.dataset.beraterStepperDot = handle;

      const isDone = activeIndex >= 0 && index < activeIndex;
      if (index === activeIndex) dot.classList.add("berater-stepper-dot--active");
      if (isDone) {
        dot.classList.add("berater-stepper-dot--done");
        const checkTemplate = this.#root.querySelector<HTMLTemplateElement>("[data-berater-stepper-check]");
        const check = checkTemplate?.content.firstElementChild?.cloneNode(true);
        if (check) {
          dot.appendChild(check);
        } else {
          dot.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        }
      } else {
        dot.textContent = String(index + 1);
      }

      item.appendChild(dot);
      list.appendChild(item);
    }
  }

  #updateSelectedStyles(panel: HTMLElement): void {
    for (const card of queryAll<HTMLElement>(panel, "[data-berater-option-card]")) {
      const input = card.querySelector<HTMLInputElement>("[data-berater-option]");
      card.classList.toggle("berater-option-card--selected", Boolean(input?.checked));
    }
  }

  #applyAnswersToInputs(): void {
    for (const answer of this.#answers) {
      const panel = this.#root.querySelector<HTMLElement>(`[data-berater-panel="${answer.stepHandle}"]`);
      if (!panel) continue;
      for (const input of queryAll<HTMLInputElement>(panel, "[data-berater-option]")) {
        input.checked = answer.values.includes(input.dataset.value ?? input.value);
      }
    }

    for (const panel of queryAll<HTMLElement>(this.#root, "[data-berater-step]")) {
      this.#updateSelectedStyles(panel);
      const continueBtn = panel.querySelector<HTMLButtonElement>("[data-berater-continue]");
      if (continueBtn) {
        const checked = panel.querySelectorAll<HTMLInputElement>("[data-berater-option]:checked");
        continueBtn.disabled = checked.length === 0;
      }
    }
  }

  #clearSelections(): void {
    for (const input of queryAll<HTMLInputElement>(this.#root, "[data-berater-option]")) {
      input.checked = false;
    }
    for (const panel of queryAll<HTMLElement>(this.#root, "[data-berater-step]")) {
      this.#updateSelectedStyles(panel);
      const continueBtn = panel.querySelector<HTMLButtonElement>("[data-berater-continue]");
      if (continueBtn) continueBtn.disabled = true;
    }
  }

  #pruneAnswersToPath(): void {
    const allowed = new Set(this.#path);
    this.#answers = this.#answers.filter((answer) => allowed.has(answer.stepHandle));
  }

  #seedHistoryFromState(panelId: string, path: string[]): void {
    this.#historyPushCount = 0;
    history.replaceState({ beraterPanel: panelId, beraterPath: path }, "");
  }

  #previousPanelTarget(): { panelId: string; path: string[] } | null {
    if (this.#panel === "result" || this.#panel === "loading") {
      const lastStep = this.#path.length ? this.#path[this.#path.length - 1] : null;
      if (lastStep && this.#steps.has(lastStep)) {
        return { panelId: lastStep, path: this.#path };
      }
      return { panelId: "intro", path: [] };
    }

    const idx = this.#path.indexOf(this.#panel);
    if (idx <= 0) return { panelId: "intro", path: [] };

    const prev = this.#path[idx - 1];
    return { panelId: prev, path: this.#path.slice(0, idx) };
  }

  #goBack(): void {
    if (this.#historyPushCount > 0) {
      this.#historyPushCount -= 1;
      history.back();
      return;
    }

    const prev = this.#previousPanelTarget();
    if (!prev || prev.panelId === this.#panel) return;

    this.#path = prev.path;
    if (prev.panelId !== "result") {
      this.#pruneAnswersToPath();
      this.#applyAnswersToInputs();
      invalidateAccessoryPreviewCache();
    }
    this.#showPanel(prev.panelId, false);
    this.#seedHistoryFromState(prev.panelId, this.#path);
  }

  #syncFromHistory(): void {
    const state = history.state as { beraterPanel?: string; beraterPath?: string[] } | null;
    if (!state?.beraterPanel) {
      if (this.#panel === "intro") return;
      this.#showPanel("intro", false);
      return;
    }

    this.#path = state.beraterPath ?? [];

    // Stale or transient targets: never strand the user on the spinner or a blank panel
    if (state.beraterPanel === "loading") {
      void renderResult(this.#root, this.#data, this.#answers);
      this.#showPanel("result", false);
      return;
    }
    if (state.beraterPanel !== "intro" && state.beraterPanel !== "result" && !this.#steps.has(state.beraterPanel)) {
      this.#showPanel("intro", false);
      return;
    }

    // Drop answers from the abandoned branch so re-selecting after "zurück" cannot
    // leak stale filters into the next forward path.
    if (state.beraterPanel !== "result") {
      this.#pruneAnswersToPath();
      this.#applyAnswersToInputs();
      invalidateAccessoryPreviewCache();
    }

    this.#showPanel(state.beraterPanel, false);
  }

  #persist(): void {
    if (this.#data.debug && (this.#panel === "result" || this.#panel === "loading")) {
      return;
    }

    writeSession(this.#data.flowHandle, {
      path: this.#path,
      answers: this.#answers,
      panel: this.#panel,
    });
  }
}

export const initBerater = (scope: ParentNode = document): void => {
  for (const root of queryAll<HTMLElement>(scope, "[data-ek-berater]")) {
    if (root.dataset.initialized === "true") continue;
    const data = parseBeraterData(root);
    if (!data) continue;

    const graphErrors = validateGraph(data);
    if (graphErrors.length) {
      const message = graphErrors.join("; ");
      console.error("[ek-berater] graph validation:", message);
      const existing = root.querySelector("[data-berater-graph-runtime-warning]");
      if (!existing) {
        const banner = document.createElement("div");
        banner.dataset.beraterGraphRuntimeWarning = "true";
        banner.setAttribute("role", "alert");
        banner.className = "bg-berry/10 text-berry px-4 py-3 text-xs text-center border-b border-berry/20";
        banner.textContent = `Berater-Flow unvollständig: ${message}`;
        root.prepend(banner);
      }
    }

    activateBeraterFunnelChrome();
    root.dataset.initialized = "true";
    new BeraterController(root, data);
  }
};
